import React, { useState } from "react";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Flx from "../../../components/layout/Flx";
import T from "../submissionTableTokens";
import { downloadSubmissionPdf } from "../submissionPdf";
import { useFetchFullSubmission } from "../useFullSubmission";

/**
 * Download this row's submission as a PDF.
 *
 * It fetches the FULL submission by id first, exactly as the expanded panel's
 * Download does, so the two produce the same document. Printing the grid row
 * directly is what made this button quietly emit a PDF with SSN, date of birth
 * and the signature blanked out: those fields are stripped by the list read
 * that populates the grid, and nothing about the resulting PDF said so.
 *
 * Only rendered where a row IS one submission. In the By Borrower lane a row
 * stands for a borrower, so a borrower with several submissions has nothing
 * unambiguous to download — `resolveRow` returns null there and the cell stays
 * empty; the nested table inside their expanded row has a button per submission.
 */
const DownloadSubmissionCell = ({ data, resolveRow }) => {
  const fetchFull = useFetchFullSubmission();
  const [busy, setBusy] = useState(false);
  const row = resolveRow ? resolveRow(data) : data;

  if (!row) return null;

  const handleDownload = async (e) => {
    // The row's own click/expand shouldn't fire too.
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await downloadSubmissionPdf(await fetchFull(row));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Flx centerY fullHeight>
      <Tooltip title="Download as PDF" arrow>
        {/* A span so the tooltip still has a live child while the button is
            disabled — MUI can't bind to a disabled element. */}
        <span>
          <IconButton
            size="small"
            aria-label="Download submission as PDF"
            disabled={busy}
            onClick={handleDownload}
            sx={{ color: T.ink500, "&:hover": { color: T.action } }}
          >
            {busy ? (
              <CircularProgress size={15} thickness={5} />
            ) : (
              <DownloadOutlinedIcon sx={{ fontSize: 17 }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Flx>
  );
};

export default DownloadSubmissionCell;
